import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealActions from '@payaca/store/deals/dealsActions';
import * as fileDownloadActions from '@payaca/store/fileDownload/fileDownloadActions';

import Button from '@payaca/components/button/Button';
import {
  ButtonColourVariant,
  ButtonStyleVariant,
} from '@payaca/components/button/enums';

import { FileDownloadTypes } from '@payaca/types/fileDownloadTypes';

import { useSelector } from '@/api/state';
import { SendSimpleCustomEmailControl } from '@/ui/components/sendSimpleCustomEmailControl/SendSimpleCustomEmailControl';
import { getCustomer, getDeal } from '@/utils/stateAccessors';
import * as documentActions from '@payaca/store/documents/documentActions';
import UntitledIcon from '@payaca/untitled-icons';
import { downloadPdfData } from '@payaca/utilities/fileUtilities';

interface Props {
  isFromMobileApp: boolean;
  dealId: number;
  documentData: {
    documentId: number;
    title: string;
  };
}

export const FormCompleteActions: FC<Props> = ({
  isFromMobileApp,
  dealId,
  documentData: { documentId, title },
}) => {
  const dispatch = useDispatch();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const deal = useSelector((state) => {
    return dealId ? getDeal(state, dealId) : null;
  });
  const customer = useSelector((state) =>
    deal?.customerId ? getCustomer(state, deal?.customerId) : undefined
  );
  const isSendingDocument = useSelector(
    (state) => state.documents.isSendingDocument
  );

  useEffect(() => {
    if (!dealId) return;
    dispatch(dealActions.requestGetDeal(dealId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [dealId]);
  useEffect(() => {
    // @ts-expect-error
    dispatch(customerActions.requestGetAndSetCurrentCustomer(deal?.customerId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal]);

  const triggerFileDownload = useCallback(
    (fileContent: string) => {
      const fileName = title || 'Document';
      downloadPdfData(fileContent, fileName);
      setIsDownloadingPdf(false);
    },
    [title]
  );

  const handleDownloadForm = useCallback(() => {
    setIsDownloadingPdf(true);
    dispatch(
      fileDownloadActions.requestDownloadFile(
        documentId,
        FileDownloadTypes.DOCUMENT,
        triggerFileDownload
      )
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'triggerFileDownload'
  }, [dispatch, documentId]);

  const handleSend = useCallback(
    ({
      sendToEmail,
      emailBody,
      sendACopy,
    }: {
      sendToEmail: string;
      emailBody: string;
      sendACopy: boolean;
    }) => {
      dispatch(
        documentActions.requestSendDocument(
          documentId,
          sendToEmail,
          emailBody,
          sendACopy,
          () => {
            setIsEmailSent(true);
          }
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'documentId'
    []
  );

  return (
    <div className={'form-external-complete-message'}>
      <UntitledIcon name="check-square.3" />
      {isFromMobileApp ? (
        <>
          <p>Document created!</p>
          <Button
            styleVariant={ButtonStyleVariant.OUTSIZE}
            onClick={handleDownloadForm}
            isProcessing={isDownloadingPdf}
            isDisabled={isDownloadingPdf}
          >
            Download PDF
          </Button>
          {isEmailSent ? (
            <h4>A PDF copy of the document has been successfully sent.</h4>
          ) : (
            <SendSimpleCustomEmailControl
              customer={customer}
              onSend={handleSend}
              isSending={isSendingDocument}
            />
          )}
          <Button
            colourVariant={ButtonColourVariant.PRIMARY}
            styleVariant={ButtonStyleVariant.OUTSIZE}
            onClick={() => {
              // @ts-expect-error
              window.location = 'Payaca://';
            }}
          >
            Return to App
          </Button>
        </>
      ) : (
        <>
          <p>Thanks for your time!</p>
          <p>This form is now complete.</p>
          <p>You can close this tab.</p>
        </>
      )}
    </div>
  );
};
